import React, { useState, useMemo, useCallback } from 'react';
import { Col } from 'react-bootstrap';
import Lightbox from 'components/Lightbox';
import { formatDateTime } from 'v3/utils/formatter';
import { FaSearch, FaDownload, FaTrash, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { cooperplaceApi } from 'services/api';
import Fallback from 'v3/components/Fallback';
import { Text, Button, Modal } from 'v3/components';
import PDFView from 'v3/components/PDFView';
import StringBuilder from 'utils/StringBuilder';
import theme from 'v3/theme';
import { useSnackbar } from 'v3/components/Snackbar';
import {
  AttachmentsHeader,
  AttachmentItemsContainer,
  Backdrop,
  DateTime,
  SubText,
  SectionContainer,
} from '../styles';
import { OccurrenceDescriptionsContainer } from '../OccurrenceInfo/styles';
import { OccurrenceAttachmentModal } from '../AttachmentsModal';
import { AttachmentNFCheckboxes } from '../AddAttachments/AttachmentNFCheckboxes';
import { getFileIcon } from '../utils';
import {
  AccordionContainer,
  AccordionHeader,
  FileIconWrapper,
  FileNameText,
  NfBadge,
  DateText,
  ActionsContainer,
  ActionButton,
  DownloadLink,
  ChevronWrapper,
  AccordionBody,
} from './styles';


export default function ShowOccurrenceAttachments({
  occurrenceId,
  medias,
  handleAddAttchmentsDirectly,
  isOccurrenceConcluded,
  occurrenceIsAnnulleed,
}) {
  const [showModal, setShowModal] = useState(false);

  const attachments = Array.isArray(medias) ? medias : [];

  return (
    <>
      <OccurrenceAttachmentModal
        isOpen={showModal}
        handleClose={() => setShowModal(false)}
        onSendDirectly={handleAddAttchmentsDirectly}
        sendDirectly
      />
      <SectionContainer>
        <AttachmentsHeader>
          <Text type="header" color="dark" weight={500}>
            Anexos
          </Text>
          {isOccurrenceConcluded === false && !occurrenceIsAnnulleed && (
            <Button size="sm" onClick={() => setShowModal(true)}>
              Adicionar anexo
            </Button>
          )}
        </AttachmentsHeader>
        <Fallback
          component={
            <Col xs={12} className="align-items-center d-flex flex-column my-2">
              <Text color={theme.colors.gray}>
                Não há anexos cadastrados para essa ocorrência.
              </Text>
            </Col>
          }
          fall={!attachments?.length}
        >
          <OccurrenceDescriptionsContainer>
            <AttachmentItemsContainer>
              {attachments?.map(attachment => (
                <AccordionAttachmentItem
                  key={attachment?.id}
                  attachment={attachment}
                  attachments={attachments}
                  occurrenceId={occurrenceId}
                />
              ))}
            </AttachmentItemsContainer>
          </OccurrenceDescriptionsContainer>
        </Fallback>
      </SectionContainer>
    </>
  );
}

function AccordionAttachmentItem({
  attachment,
  attachments,
}) {
  const customReactModalStyle = { overlay: { zIndex: 2000 } };

  const [isOpen, setIsOpen] = useState(true);
  const [isShowingModal, setShowingModal] = useState(false);

  const ext = (attachment?.media?.name || '').split('.').pop().toLowerCase();
  const { Icon, color } = getFileIcon(ext);

  const displayName = useMemo(() => {
    const desc = attachment?.media?.description || '';
    const extList = ['png', 'jpeg', 'jpg', 'pdf', 'xls', 'xlsx', 'xml'];
    if (desc && !extList.some(e => desc.trim().toLowerCase().endsWith(e))) return desc;
    return attachment?.media?.name?.split(':').pop() || desc;
  }, [attachment]);

  const nfCount = attachment?.nfDocuments?.length || 0;
  const hasNfDocs = Array.isArray(attachment?.nfDocuments) && attachment.nfDocuments.length > 0;
  const isExpandable = hasNfDocs;

  const attachmentsWithoutPDF = useMemo(
    () =>
      attachments?.filter(item => {
        if (!item.media) return false;
        return item.media.name?.split('.').pop() !== 'pdf';
      }),
    [attachments]
  );

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    attachments: attachmentsWithoutPDF,
    index: 0,
  });

  const lightboxIndexes = useMemo(() => {
    const len = lightbox.attachments?.length || 1;
    return {
      next: (lightbox.index + 1) % len,
      previous: (lightbox.index + len - 1) % len,
    };
  }, [lightbox.index, lightbox.attachments]);

  const lightboxTitle = useMemo(() => {
    const doc = lightbox.attachments[lightbox.index];
    const builder = new StringBuilder(' ');
    builder
      .append(`${lightbox.index + 1}/${lightbox.attachments?.length}`)
      .append('|')
      .append(doc?.description || 'Sem descrição')
      .append(`(${formatDateTime(doc?.createdAt)})`);
    return builder.build();
  }, [lightbox.index, lightbox.attachments]);

  function handlePreview() {
    if (ext === 'pdf') {
      setShowingModal(true);
    } else if (!['xlsx', 'xls', 'xml'].includes(ext)) {
      const index = attachmentsWithoutPDF?.findIndex(a => a.id === attachment.id) ?? 0;
      setLightbox(prev => ({ ...prev, index: Math.max(0, index), isOpen: true }));
    }
  }

  const canPreview = !['xlsx', 'xls', 'xml'].includes(ext);

  return (
    <>
      <Modal
        show={isShowingModal}
        onHide={() => setShowingModal(false)}
        heading="Visualizar PDF"
        body={<PDFView file={attachment?.media?.full_path} />}
      />

      {lightbox.isOpen && (
        <Lightbox
          mainSrc={lightbox.attachments[lightbox.index]?.media?.full_path}
          nextSrc={lightbox.attachments[lightboxIndexes.next]?.media?.full_path}
          prevSrc={lightbox.attachments[lightboxIndexes.previous]?.media?.full_path}
          onCloseRequest={() => setLightbox(prev => ({ ...prev, isOpen: false }))}
          onMovePrevRequest={() =>
            setLightbox(prev => ({ ...prev, index: lightboxIndexes.previous }))
          }
          onMoveNextRequest={() =>
            setLightbox(prev => ({ ...prev, index: lightboxIndexes.next }))
          }
          imageTitle={lightboxTitle}
          reactModalStyle={customReactModalStyle}
        />
      )}

      <AccordionContainer>
        {/* Accordion header */}
        <AccordionHeader
          isExpandable={isExpandable}
          role={isExpandable ? 'button' : undefined}
          tabIndex={isExpandable ? 0 : undefined}
          onKeyDown={e => isExpandable && e.key === 'Enter' && setIsOpen(o => !o)}
          onClick={() => isExpandable && setIsOpen(o => !o)}
        >
          <FileIconWrapper>
            <Icon size={18} color={color} />
          </FileIconWrapper>

          <FileNameText type="regular" color="dark">
            {displayName}
          </FileNameText>

          {/* NF count badge */}
          {nfCount > 0 && (
            <NfBadge>
              {nfCount} NF{nfCount > 1 ? 's' : ''}
            </NfBadge>
          )}

          {/* Date */}
          <DateText type="label" color="gray">
            {formatDateTime(attachment?.media?.createdAt)}
          </DateText>

          {/* Action buttons — stop propagation so they don't toggle accordion */}
          <ActionsContainer onClick={e => e.stopPropagation()}>
            {canPreview && (
              <ActionButton type="button" onClick={handlePreview} title="Visualizar">
                <FaSearch size={13} color="#666" />
              </ActionButton>
            )}
            <DownloadLink href={attachment?.media?.full_path} download title="Baixar">
              <FaDownload size={13} color="#666" />
            </DownloadLink>
          </ActionsContainer>

          {/* Chevron */}
          {isExpandable && (
            <ChevronWrapper>
              {isOpen ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
            </ChevronWrapper>
          )}
        </AccordionHeader>

        {/* Accordion body */}
        {isOpen && hasNfDocs && (
          <AccordionBody>
            <AttachmentNFCheckboxes
              readOnly
              readOnlyNfDocuments={attachment.nfDocuments}
            />
          </AccordionBody>
        )}
      </AccordionContainer>
    </>
  );
}

// Kept for backward compatibility — used by OccurrencesCard.js
export function OccurrenceAttachment({
  attachment,
  attachments,
  occurrenceId,
  refetchAttachments,
  hasPermissionToRemove = true,
}) {
  const customReactModalStyle = {
    overlay: {
      zIndex: 2000,
    },
  };
  const [isHovering, setHovering] = useState(false);
  const [isShowingModal, setShowingModal] = useState(false);
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);

  const attachmentsWithoutPDF = useMemo(() => {
    return attachments?.filter(item => {
      if (item.media) {
        const filterPDF = item?.media?.name?.split('.');
        if (filterPDF[filterPDF?.length - 1] === 'pdf') return null;
        return item;
      }
    });
  }, [attachments]);

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    attachments: attachmentsWithoutPDF,
    index: 0,
  });

  async function removeAttachment(attachmentId) {
    try {
      setLoading(true);
      await cooperplaceApi.delete(
        `occurrence-media/${occurrenceId}/${attachmentId}`
      );
      await refetchAttachments();
    } catch (error) {
      snackbar.show('Não foi possível excluir o anexo', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  const lightboxIndexes = useMemo(() => {
    const next = (lightbox.index + 1) % lightbox.attachments?.length;
    const previous =
      (lightbox.index + lightbox.attachments.length - 1) %
      lightbox.attachments.length;
    return { next, previous };
  }, [lightbox.index, lightbox.attachments]);

  function onClickPreview(id) {
    const index = id ? attachmentsWithoutPDF.findIndex(a => a.id === id) : 0;

    setLightbox(prev => ({
      ...prev,
      index,
      attachments: attachmentsWithoutPDF,
      isOpen: true,
    }));
  }

  function handleClick() {
    const fullPathArray = attachment?.media?.name?.split('.');

    if (fullPathArray[fullPathArray?.length - 1] === 'pdf') {
      setShowingModal(true);
    } else {
      onClickPreview(attachment.id);
    }
  }

  function getImagePath(index, alwaysDisplay) {
    const isSameAsIndex =
      lightbox.index === lightboxIndexes.next &&
      lightbox.index === lightboxIndexes.previous;

    if (!alwaysDisplay && isSameAsIndex && lightbox.attachments.length === 1) {
      return null;
    }

    return lightbox.attachments[index]?.media?.full_path;
  }

  function getDocumentMetadata() {
    const document = lightbox.attachments[lightbox.index];
    const type = document?.description || 'Sem descrição';
    const createdAt = formatDateTime(document?.createdAt);

    return { type, createdAt };
  }

  const lightboxTitle = useMemo(() => {
    const builder = new StringBuilder(' ');
    const documentMetadata = getDocumentMetadata();
    builder
      .append(`${lightbox.index + 1}/${lightbox.attachments?.length}`)
      .append('|')
      .append(documentMetadata.type)
      .append(`(${documentMetadata.createdAt})`);

    return builder.build();
  }, [lightbox.index, lightbox.attachments]);

  return (
    <div>
      <Modal
        show={isShowingModal}
        onHide={() => setShowingModal(false)}
        heading="Visualizar PDF"
        body={
          <PDFView
            file={attachment?.media?.full_path}
          />
        }
      />
      <div
        className="position-relative"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        style={{
          width: 240,
          height: 120,
          background: `url(${attachment?.media?.name?.split('.').pop() === 'pdf'
            ? '/images/default-doc.png'
            : attachment?.media?.name?.split('.').pop() === 'xlsx'
              ? '/images/default-excel.png'
              : attachment?.media?.name?.split('.').pop() === 'xls'
                ? '/images/default-excel.png'
                : attachment?.media?.full_path
            }) center center no-repeat`,
          backgroundSize: 'cover,contain',
        }}
      >
        {lightbox.isOpen && (
          <Lightbox
            mainSrc={getImagePath(lightbox.index, true)}
            nextSrc={getImagePath(lightboxIndexes.next)}
            prevSrc={getImagePath(lightboxIndexes.previous)}
            onCloseRequest={() =>
              setLightbox(prev => ({ ...prev, isOpen: false }))
            }
            onMovePrevRequest={() =>
              setLightbox(prev => ({
                ...prev,
                index: lightboxIndexes.previous,
              }))
            }
            onMoveNextRequest={() =>
              setLightbox(prev => ({
                ...prev,
                index: lightboxIndexes.next,
              }))
            }
            imageTitle={lightboxTitle}
            reactModalStyle={customReactModalStyle}
          />
        )}
        {isHovering && (
          <Backdrop>
            {attachment?.media?.name?.split('.').pop() !== 'xlsx' &&
              attachment?.media?.name?.split('.').pop() !== 'xls' &&
              attachment?.media?.name?.split('.').pop() !== 'xml' && (
                <button type="button" onClick={handleClick}>
                  <FaSearch color="#fff" size={32} />
                </button>
              )}
            <a
              href={attachment?.media?.full_path}
              download
            >
              <FaDownload color="#fff" size={32} />
            </a>
            {hasPermissionToRemove && (
              <Button
                variant="transparent"
                loading={loading}
                size={0}
                onClick={() => removeAttachment(attachment?.attachmentId)}
              >
                <FaTrash size={32} />
              </Button>
            )}
          </Backdrop>
        )}
        {
          attachment?.media?.name?.split('.').pop() === 'xml' && (
            <SubText>
              <Text type="label" color="white">
                Arquivo XML
              </Text>
            </SubText>)
        }
        <DateTime>
          <Text type="label" color="white">
            {formatDateTime(attachment?.media?.createdAt)}
          </Text>
        </DateTime>
      </div>
    </div>
  );
}
